<template>
    <div class="overflow:hidden" :class="{ 'max-h:50': collapsible && truncated }">
        <div class="flex flex:col gap:12" ref="container">
            <template :key="item" v-for="(item, idx) in items">
                <div class="f:16 lh:1.5 color:#333 break-word" v-if="item.type === 1">
                    <span class="color:primary" v-if="!idx && to">{{ to }}&nbsp;</span>
                    <topic-input :comment="comment" :editable="editable" @focus="emit('focus')" :placeholder="placeholder" ref="inputs" @submit="emit('submit')" v-model="item.text"></topic-input>
                    <div class="mt:16" v-if="item.urls?.length">
                        <div :class="{ 'mt-2': idx }" :key="url" v-for="(url,idx) in item.urls">
                            <a class="max-w:452 b:1|#EBEBEB|solid r:12 overflow:hidden flex flex:col" :href="url.url" target="_blank" v-if="url.image_url">
                                <img alt="" class="w:full" :src="url.image_url" />
                                <span class="lines:1 p:12|16|4|16 color:#333333">{{ url.title }}</span>
                                <span class="p:0|16|12|16 overflow:hidden">
                                    <span class="lines:1 color:#6F737D">{{ url.url }}</span>
                                </span>
                            </a>
                            <a class="b:1|#EBEBEB|solid r:12 overflow:hidden inline-flex max-w:full" :href="url.url" target="_blank" v-else>
                                <span class="bg:#EEF3FB">
                                    <span class="flex ai:center jc:center h:52 w:52">
                                        <i class="fa-solid fa-link color:#333333"></i>
                                    </span>
                                </span>
                                <span class="p:16 overflow:hidden">
                                    <span class="lines:1 color:#6F737D">{{ url.url }}</span>
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
                <div class="color:primary" v-else-if="!idx && to">{{ to }}</div>
                <div class="flex ai:start gap:8" v-if="item.type === 2">
                    <div>
                        <img alt="" :class="comment && editable ? 'max-h:72 r:8' : 'max-h:600 r:20'" :src="item.image_url || item.data" />
                    </div>
                    <img alt="" class="cursor:pointer w:24" @click="remove(idx)" src="image/icon_round_close.svg" v-if="editable" />
                </div>
                <div class="flex ai:start gap:8" v-else-if="item.type === 3">
                    <div>
                        <video class="max-w:full max-h:600 r:20 bg:black" controls>
                            <source :src="item.video_url || item.data" />
                        </video>
                    </div>
                    <img alt="" class="cursor:pointer w:24" @click="remove(idx)" src="image/icon_round_close.svg" v-if="editable" />
                </div>
                <div v-else-if="item.type === 4 && !item.deleted">
                    <a class="block max-w:452 r:20 p:12|16 b:1|solid|#EBEBEB {mt:8}>div+div" :href="`topic/${item.part_path || item.part_id}/${item.target}`" target="_blank">
                        <div class="lh:1.5 f:16 f:bold color:#333333">{{ item.title }}</div>
                        <div class="color:#5E6472 lines:1">
                            {{ item.text }}
                        </div>
                        <div class="post-func-buttons">
                            <topic-like-buttons
                                :dislike="item.dislike"
                                :dislike-count="item.dislike_count"
                                :like="item.like"
                                :like-count="item.like_count"
                                :observe="item.observe"
                                :observe-count="item.observe_count"
                                readonly
                            ></topic-like-buttons>
                        </div>
                    </a>
                </div>
            </template>
        </div>
        <span class="color:#A9A9A9" v-if="modified">（已編輯）</span>
    </div>
    <button class="f:14 color:primary" @click="truncated = false" type="button" v-if="collapsible && truncated">更多</button>
</template>

<script setup>
    const global = useGlobal();

    const props = defineProps({
        collapsible: { default: false, type: Boolean },
        comment: { default: false, type: Boolean },
        editable: { default: false, type: Boolean },
        modelValue: { default: [], type: Array },
        modified: { type: String },
        placeholder: { type: String },
        to: { type: String }
    });

    const container = ref();
    const inputs = ref();
    const items = ref(props.modelValue);
    const truncated = ref(false);

    const add = (item) => {
        const last = items.value.length - 1;

        if (last >= 0 && items.value[last].type === 1 && !items.value[last].text) {
            items.value.splice(last, 0, item);
        } else {
            items.value.push(item, { text: "", type: 1 });
        }
    };

    const addImages = (images) => {
        Array.from(images).forEach((image) => {
            if (!image.type.startsWith("image/")) {
                return;
            }

            const reader = new FileReader();

            reader.onload = () => {
                add({
                    type: 2,
                    name: image.name,
                    data: reader.result
                });
            };

            reader.readAsDataURL(image);
        });
    };

    const addRef = (target) => {
        if (!target || items.value.some((item) => item.type === 4)) {
            return;
        }

        api("api/topic/get", { topic_id: target }).then((res) => {
            if (res.success && res.data.status === 1) {
                add({
                    type: 4,
                    target,
                    title: res.data.title,
                    text: res.data.content.find((item) => item.type === 1)?.text,
                    image_url: res.data.content.find((item) => item.type === 2)?.image_url,
                    like: res.data.like,
                    like_count: res.data.like_count,
                    dislike: res.data.dislike,
                    dislike_count: res.data.dislike_count,
                    observe: res.data.observe,
                    observe_count: res.data.observe_count
                });

                emit("loaded");
            }
        });
    };

    const addVideos = (videos) => {
        Array.from(videos).forEach((video) => {
            if (!video.type.startsWith("video/")) {
                return;
            }

            const reader = new FileReader();

            reader.onload = () => {
                const media = new Audio(reader.result);

                media.onloadedmetadata = () => {
                    if (media.duration > 60) {
                        global.error("僅能上傳一分鐘內影片");
                    } else {
                        add({
                            type: 3,
                            name: video.name,
                            data: reader.result
                        });
                    }
                };
            };

            reader.readAsDataURL(video);
        });
    };

    const emit = defineEmits(["focus", "loaded", "submit", "update:modelValue"]);

    const focus = () => {
        if (inputs.value.length) {
            inputs.value[inputs.value.length - 1].focus();
        } else {
            inputs.value.focus();
        }
    };

    const remove = (idx) => {
        items.value.splice(idx, 1);

        if (idx > 0) {
            const prev = items.value[idx - 1];
            const curr = items.value[idx];

            if (prev.type === 1 && curr.type === 1) {
                prev.text = `${prev.text}${curr.text}`;
            }

            items.value.splice(idx, 1);
        }
    };

    defineExpose({ addImages, addRef, addVideos, focus });

    onMounted(() => nextTick(() => truncated.value = container.value.offsetHeight > 50));

    watch(items, () => {
        emit("update:modelValue", items.value);
    }, { deep: true });

    watch(() => props.modelValue, () => {
        items.value = props.modelValue;
    });
</script>
